"use client";

import { useEffect } from "react";

import {
  classifyErrorLevel,
  isStaleChunkError,
  markChunkLoadFailure,
  shouldDropError,
  NOISE_ERROR_RE as SHARED_NOISE_ERROR_RE,
  CHUNK_ERROR_RE as SHARED_CHUNK_ERROR_RE,
} from "@/lib/error-triage";
import { appVersion } from "@/lib/app-version";
import { clarityUpgrade } from "@/lib/clarity";
import { normaliseErrorPage, type ErrorTrigger } from "@/lib/error-page";

/**
 * Mounted globally — listens for uncaught errors and unhandled promise
 * rejections, then ships them to `POST /api/client-errors`. The endpoint
 * is rate-limited per IP and dedupes by fingerprint, so even a render
 * loop can't spam the table.
 *
 * Why a custom reporter instead of Sentry / LogRocket?
 *   - Free-tier vendors all add ~50 KB+ of JS. This reporter is < 1 KB.
 *   - The data goes straight into our own D1 table — no third-party data
 *     sharing, no GDPR processor agreement needed.
 *   - Errors land in /admin/errors so the same admin who handles the
 *     order queue can also triage bugs without bouncing to a SaaS UI.
 *
 * Captured channels:
 *   - `window.onerror` — synchronous JS exceptions
 *   - `window.onunhandledrejection` — async/Promise failures
 *   - a CAPTURE-phase listener for failed `/_next/static/` bundles (those
 *     events don't bubble and carry no message, so `onerror` never sees them)
 *   - every route-segment `error.tsx` + `app/global-error.tsx`, which call
 *     the exported `reportError` directly
 *   - `public/sw.js`, which POSTs to the same endpoint from its own scope
 *
 * Limits applied client-side (additional caps server-side):
 *   - Drops the same fingerprint within 60 s — stops a hot loop from
 *     queueing 1000 reports during a single render.
 *   - Drops third-party-injection noise and bare network cancellations, and
 *     downgrades self-healing conditions — all via `lib/error-triage.ts`,
 *     which `POST /api/client-errors` applies identically.
 *   - Undeliverable reports (offline, HTTP 429) go to a bounded localStorage
 *     retry queue rather than being lost.
 *
 * There is deliberately NO suppression latch during page unload — see the
 * note above `QUEUE_KEY` for why that was actively harmful.
 */

const REPORT_ENDPOINT = "/api/client-errors";

/** Coarse fingerprint for client-side dedupe. Doesn't need to match the
 *  server's fingerprint exactly — server has the authoritative dedupe. */
function clientFingerprint(message: string, source: string): string {
  return `${source}:${message.slice(0, 200)}`;
}

/**
 * SEC-2 — cut the query string / fragment off a URL before it leaves the browser.
 *
 * The authoritative strip is `redactStoredUrl` in `lib/app-errors.ts`, at the
 * single point of storage (this endpoint is public, so a stale bundle or a bare
 * `curl` must not be able to bypass it). This is the same rule applied on the
 * client so a report is never in flight carrying a magic-link `?token=` or a
 * Stripe `?session_id=` in the first place — the `lib/error-triage.ts` rule that
 * both sides filter, never only one.
 *
 * Duplicated rather than imported because `lib/app-errors.ts` pulls in Drizzle
 * and the D1 schema; importing it here would drag all of that into the browser
 * bundle. `tests/unit/review-2026-08-03/error-url-redaction.spec.ts` pins the two
 * implementations to the same expression so they cannot drift.
 */
export function stripUrlQueryForReport(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

const DEDUPE_WINDOW_MS = 60_000;
const recentReports = new Map<string, number>();

/**
 * Test-only: clear the dedupe window.
 *
 * Without this, every spec in a file has to invent a unique message string,
 * and the day someone reuses one they get zero beacons and it looks like a
 * filter regression rather than test bleed.
 */
export function resetReportDedupeForTests(): void {
  recentReports.clear();
}

function shouldReport(fingerprint: string): boolean {
  const now = Date.now();
  const last = recentReports.get(fingerprint);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;
  recentReports.set(fingerprint, now);
  // Keep map bounded so it can't leak.
  if (recentReports.size > 200) {
    for (const [k, t] of recentReports) {
      if (now - t > DEDUPE_WINDOW_MS) recentReports.delete(k);
    }
  }
  return true;
}

/**
 * The noise/severity rules live in `lib/error-triage.ts` so the PUBLIC ingest
 * route applies exactly the same ones — filtering only here meant a stale tab
 * across a deploy (or a direct `curl`) wrote unfiltered rows straight into the
 * table. Re-exported for the existing regression specs that import them here.
 */
export const NOISE_ERROR_RE = SHARED_NOISE_ERROR_RE;

/*
 * NOTE — there is deliberately NO "suppress everything while unloading" latch.
 *
 * A previous revision set a flag on `pagehide` AND `beforeunload`, cleared only
 * by `pageshow`. That was wrong twice over:
 *   - `beforeunload` fires on ATTEMPTED navigation, and the document routinely
 *     survives it (download links — the admin has several CSV/xlsx exports —
 *     `mailto:`/`tel:` links, a cancelled navigation, a hung target server).
 *     `pageshow` does NOT fire in those cases, so the flag latched `true` for
 *     the rest of the document's life and silently dropped EVERY later error.
 *     On an admin session that is hours of blindness.
 *   - It defeated the entire point of `sendBeacon`, which exists precisely to
 *     deliver a report during unload.
 * The cancellation noise it was meant to stop is already handled properly, by
 * message, in `BENIGN_NETWORK_RE`. Do not reintroduce a latch here.
 */

/**
 * Reports that could not be delivered (offline, a 429, a dead endpoint).
 *
 * Dropping them instead — as an earlier revision did for offline devices —
 * loses the signal permanently, which matters most for exactly the sessions we
 * understand least: the installed PWA, flaky mobile, and any client that
 * tripped the ingest rate limit. Bounded and time-boxed so it can never grow
 * into a storage problem or replay ancient noise after a fix has shipped.
 */
const QUEUE_KEY = "fm:error-report-queue";
const QUEUE_FLUSHED_KEY = "fm:error-report-flushed-at";
const QUEUE_MAX = 20;
const QUEUE_TTL_MS = 24 * 60 * 60 * 1000;
/** Give up on an item after this many delivery attempts. */
const QUEUE_MAX_TRIES = 3;
/** Never drain more often than this, however many tabs/mounts occur. */
const QUEUE_FLUSH_INTERVAL_MS = 60_000;

interface QueuedReport {
  /** First-enqueued time. Preserved across retries so the TTL can expire. */
  at: number;
  tries: number;
  body: string;
}

function readQueue(): QueuedReport[] {
  try {
    const raw = window.localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    // Untrusted: another script (or a previous version of this one) may have
    // written anything here.
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - QUEUE_TTL_MS;
    return (parsed as QueuedReport[]).filter(
      (r) =>
        r &&
        typeof r.body === "string" &&
        typeof r.at === "number" &&
        r.at > cutoff &&
        (typeof r.tries !== "number" || r.tries < QUEUE_MAX_TRIES)
    );
  } catch {
    return [];
  }
}

function writeQueue(items: QueuedReport[]): void {
  try {
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify(items.slice(-QUEUE_MAX)));
  } catch {
    /* storage full or blocked (private mode) — the queue is best-effort */
  }
}

function enqueueReport(body: string, prev?: QueuedReport): void {
  writeQueue([
    ...readQueue(),
    // Keep the ORIGINAL timestamp on a retry. Re-stamping it made the 24h TTL
    // unreachable, so a permanently-failing item lived forever.
    { at: prev?.at ?? Date.now(), tries: (prev?.tries ?? 0) + 1, body },
  ]);
}

/**
 * Drain the queue: SEQUENTIALLY, and stop at the first 429.
 *
 * Three things have to hold or this becomes a self-inflicted flood, since the
 * ingest limiter is 60/hr/IP:
 *  - sequential + abort-on-429, so 20 stranded items can't fire 20 parallel
 *    POSTs, get 20 rejections, and re-queue all 20 for the next page load;
 *  - a `tries` cap, so an item can't be retried forever;
 *  - a min-interval, because this runs on EVERY mount of the root reporter
 *    (i.e. every page load, in every tab).
 */
async function flushQueue(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const last = Number(window.localStorage.getItem(QUEUE_FLUSHED_KEY) ?? "0");
    if (Number.isFinite(last) && Date.now() - last < QUEUE_FLUSH_INTERVAL_MS) return;
    window.localStorage.setItem(QUEUE_FLUSHED_KEY, String(Date.now()));
  } catch {
    /* storage blocked — fall through and just attempt the drain */
  }

  const items = readQueue();
  if (items.length === 0) {
    // `readQueue` filters out expired and try-exhausted entries, so write the
    // pruned list back — otherwise a dead item sits in storage forever,
    // re-filtered on every read but never actually removed.
    writeQueue([]);
    return;
  }
  writeQueue([]);

  for (let i = 0; i < items.length; i++) {
    const item = items[i]!;
    try {
      const res = await fetch(REPORT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: item.body,
        keepalive: true,
      });
      if (res.status === 429) {
        // Limiter is saturated — stop, and keep this item AND the rest.
        for (const remaining of items.slice(i)) {
          enqueueReport(remaining.body, remaining);
        }
        return;
      }
    } catch {
      enqueueReport(item.body, item);
    }
  }
}

interface ReportErrorInput {
  level?: "error" | "warning" | "info";
  source: "client" | "react-boundary" | "unhandled-rejection";
  message: string;
  stack?: string | null;
  url?: string | null;
  context?: Record<string, unknown> | null;
  /**
   * HOW this was raised. `source` is too coarse to tell a resource-load failure
   * from a `window.onerror`, or a root-layout boundary from a route one.
   */
  trigger?: ErrorTrigger;
}

/**
 * Public helper — call from React error boundaries / try/catch blocks
 * to forward an error explicitly. Same dedupe + rate-limit applies.
 */
export function reportError(input: ReportErrorInput): void {
  if (typeof window === "undefined") return;
  // Drop known third-party-injection noise (in-app browser bridges, autofill
  // shims, extension scripts) and aborted network requests — both are
  // unfixable and were the bulk of what made the admin log untrustworthy.
  // `source` matters: a react-boundary report is never dropped as "benign
  // network", because reaching a boundary means the user SAW a broken page.
  const triage = {
    message: input.message,
    url: input.url,
    stack: input.stack,
    source: input.source,
  };
  if (shouldDropError(triage)) return;
  const fp = clientFingerprint(input.message, input.source);
  if (!shouldReport(fp)) return;
  // `page` is the single most useful missing field: for a window.onerror the
  // `url` column holds the SCRIPT that threw (a hashed chunk filename), which
  // told the admin nothing about where it happened.
  //
  // `context.page` is kept for back-compat — the server still reads it as a
  // fallback for a bundle from before the top-level `page` existed, and
  // ERRLOG-11 pins it as the "page is always recorded alongside the script"
  // guard.
  //
  // SEC-2: it used to be `pathname + search`, justified by a comment claiming
  // "the SERVER strips it before anything is stored". That was FALSE — the
  // server only ever ran `normaliseErrorPage` over the top-level `page`, so
  // `context.page` carried a `/orders?token=…` magic-link token or a
  // `?session_id=…` Stripe session verbatim into `app_errors`, which
  // `/admin/errors` renders in full. It is now the same query-free value as the
  // top-level `page`. `lib/app-errors.ts` redacts URL-shaped context values on
  // the way in as well, so both sides filter — never only one.
  const context = {
    ...(input.context ?? {}),
    page: normaliseErrorPage(window.location.pathname),
  };
  const level = classifyErrorLevel(triage, input.level ?? "error");

  // Ask Clarity to retain THIS session, so the replay of a real error still
  // exists when someone comes to diagnose it. Placed after `shouldDropError`
  // and `shouldReport` on purpose: it inherits the same definition of "real"
  // that /admin/errors uses, so the two can never disagree about which sessions
  // were worth keeping. Gated on `level === "error"` — the hydration family
  // (#418/#419/#423/#425) is recorded as `warning` precisely because React
  // recovered and the visitor saw correct output; upgrading those would flood
  // the prioritised set with sessions that have nothing to show.
  if (level === "error") clarityUpgrade("js-error");

  const body = JSON.stringify({
    level,
    source: input.source,
    message: input.message,
    stack: input.stack ?? null,
    // SEC-2. This is the choke point every reporting site funnels through —
    // `window.onerror`'s `ev.filename`, the resource-load `src`, an explicit
    // `reportError` from a boundary, and the `window.location.href` fallback that
    // EVERY unhandled rejection takes (a rejection never supplies a url, which is
    // how a customer's live credential got here in the first place).
    url: stripUrlQueryForReport(input.url ?? window.location.href),
    userAgent: navigator.userAgent,
    // Query stripped here too, so a report is never in flight carrying a
    // magic-link token or a Stripe session id even before the server sees it.
    page: normaliseErrorPage(window.location.pathname),
    triggerKind: input.trigger ?? "manual",
    // The version of the BUNDLE THAT THREW — deliberately the inlined literal
    // and not anything fetched. A tab still running a pre-deploy bundle must
    // report the OLD version; that disagreement is the signal. Queued reports
    // therefore keep the version of the build that created them, which is
    // exactly right: the body is serialised once and replayed verbatim.
    buildId: appVersion(),
    context,
  });

  // Offline: don't even attempt — queue it so the report survives, instead of
  // being discarded (which is what an earlier revision did).
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    enqueueReport(body);
    return;
  }

  // `sendBeacon` ONLY when the page is going away.
  //
  // It exposes no response, so a 429 from the ingest limiter is invisible and
  // the report is lost silently — which made the retry queue below dead code on
  // the primary path in every major browser. `fetch` is status-observable, so
  // use it whenever we can still see the answer, and reserve sendBeacon for the
  // hidden/unloading case it actually exists for.
  const pageGoingAway = typeof document !== "undefined" && document.visibilityState === "hidden";
  if (pageGoingAway) {
    try {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon && navigator.sendBeacon(REPORT_ENDPOINT, blob)) {
        return;
      }
    } catch {
      // ignore and fall through to fetch
    }
  }
  fetch(REPORT_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  })
    .then((res) => {
      // The ingest limiter is 60/hr/IP. Without this, a burst would silently
      // destroy every report after the 60th — including unrelated real ones.
      if (res.status === 429) enqueueReport(body);
    })
    .catch(() => enqueueReport(body));
}

/**
 * A chunk-load failure — "Loading chunk 4328 failed", "ChunkLoadError", or a
 * `*.undefined.js` URL — almost always means the client is running a STALE app
 * version whose webpack runtime references chunk hashes a newer deploy has
 * rotated away (common after a deploy while a tab/PWA was open). A single reload
 * pulls the fresh HTML + chunk manifest and fixes it. Guarded so a genuinely
 * missing chunk can't cause an infinite reload loop.
 */
export const CHUNK_ERROR_RE = SHARED_CHUNK_ERROR_RE;

/**
 * Input types that carry nothing the visitor typed.
 *
 * These matter as much as the ones we DO count: a CSRF `hidden` field, a ticked
 * consent `checkbox` and a `submit` button all have non-empty values on pages
 * with no typed input at all. Counting them would suppress the self-heal
 * site-wide — the opposite failure to the one this guard exists to prevent.
 */
const NON_TEXT_INPUT_TYPES = new Set([
  "hidden",
  "submit",
  "button",
  "reset",
  "image",
  "checkbox",
  "radio",
  "range",
  "color",
  "file",
]);

/**
 * True when the page holds text the visitor typed and has not yet submitted.
 *
 * Reads the live `.value` PROPERTY, not the `value` attribute: a controlled
 * React input updates the former and leaves the latter at its server-rendered
 * default, so an attribute check would see an empty box for someone who has
 * filled in the whole form.
 */
export function hasUnsavedFormInput(doc: Document): boolean {
  try {
    for (const el of Array.from(doc.querySelectorAll("input, textarea"))) {
      const field = el as HTMLInputElement | HTMLTextAreaElement;
      const type = (field as HTMLInputElement).type?.toLowerCase() ?? "text";
      if (NON_TEXT_INPUT_TYPES.has(type)) continue;
      if (typeof field.value === "string" && field.value.trim() !== "") return true;
    }
  } catch {
    // A DOM we cannot read is not evidence that anything was typed — fall
    // through to `false` so the self-heal still works.
  }
  return false;
}

export function reloadOnceForChunkError(message: string, stack?: string | null): boolean {
  if (typeof window === "undefined" || !message) return false;
  // `isStaleChunkError` (not a bare message regex) so the ambiguous minified
  // `n[e] is not a function` shape only triggers a reload when a `/_next/static/`
  // resource has actually failed to load in this session. Reloading the page
  // out from under someone because of a genuine bug is its own defect.
  if (!isStaleChunkError(message, stack)) return false;
  // …and so is reloading one out from under someone who is mid-form. The
  // checkout store is NOT persisted (only the cart is), and `CheckoutClient`
  // snapshots to sessionStorage just before the Stripe redirect — so a reload
  // during data entry destroys the name, phone, address and gift message the
  // customer has typed, while the cart survives to make it look like a glitch
  // rather than data loss. `lib/error-triage.ts` notes a dropped route-prefetch
  // on a flaky mobile connection trips this too, which is exactly the customer
  // least able to afford retyping an address.
  //
  // Checked BEFORE the sessionStorage budget below, so suppressing here never
  // spends the one-shot allowance a later clean-page failure needs.
  if (hasUnsavedFormInput(document)) return false;
  try {
    const KEY = "fm-chunk-reload-at";
    const COUNT_KEY = "fm-chunk-reload-count";
    const last = Number(window.sessionStorage.getItem(KEY) || "0");
    // One reload per 20 s: a stale deploy needs exactly one. If it re-fails
    // straight after (a genuinely missing chunk), stop and let the error show
    // rather than loop the page.
    if (Date.now() - last < 20_000) return false;
    // …and never more than twice per session. The time window alone still
    // allowed a permanently-missing chunk to reload the page every 20 seconds
    // indefinitely, which is unusable rather than self-healing.
    const count = Number(window.sessionStorage.getItem(COUNT_KEY) || "0");
    if (Number.isFinite(count) && count >= 2) return false;
    window.sessionStorage.setItem(KEY, String(Date.now()));
    window.sessionStorage.setItem(COUNT_KEY, String((Number.isFinite(count) ? count : 0) + 1));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

/**
 * Best-effort description of whatever a promise rejected WITH.
 *
 * A rejection reason is `any` — it is frequently a Response, a plain object, or
 * a DOMException rather than an Error. The old code collapsed every one of
 * those to the literal string "Unhandled promise rejection", which deduped them
 * all into a single, permanently useless row. Extracting the shape gives the
 * admin something to search for; capped so a huge object can't bloat the table.
 */
function describeRejection(reason: unknown): string {
  if (reason instanceof Error) return reason.message || reason.name;
  if (typeof reason === "string") return reason;
  if (reason == null) return "Unhandled promise rejection (no reason)";
  if (typeof reason === "object") {
    const o = reason as Record<string, unknown>;
    if (typeof o.message === "string" && o.message) return o.message;
    // A rejected fetch Response is a common one worth naming precisely.
    //
    // SEC-2: `response.url` goes into the MESSAGE, which is stored verbatim and
    // rendered in full by /admin/errors — so a rejected fetch to
    // `/api/orders/lookup?token=…` wrote the magic-link token into the log via a
    // field the `url`/`page`/`context` redaction never touched. Strip it here, at
    // the point the string is built; `redactUrlQueriesInText` in
    // `lib/app-errors.ts` is the server-side belt for every other message source.
    if (typeof o.status === "number") {
      const where =
        typeof o.url === "string" ? ` ${stripUrlQueryForReport(o.url).slice(0, 300)}` : "";
      return `Unhandled rejection: HTTP ${o.status}${where}`;
    }
    // KEYS ONLY, never values. Stringifying the whole reason would copy
    // whatever it holds — a parsed API response, form state, a customer's
    // email — verbatim into `app_errors`, which the server-side PII rule
    // explicitly forbids. The shape is enough to identify the rejection.
    try {
      const keys = Object.keys(o).slice(0, 12).join(", ");
      return keys
        ? `Unhandled rejection: object with keys {${keys}}`
        : "Unhandled promise rejection (empty object)";
    } catch {
      return "Unhandled promise rejection (uninspectable reason)";
    }
  }
  return `Unhandled rejection: ${String(reason).slice(0, 200)}`;
}

/** Mount once near the root. Safe to mount multiple times — the global
 *  listeners just register and overwrite previous ones. */
export function ClientErrorReporter() {
  useEffect(() => {
    function onError(ev: ErrorEvent) {
      // Skip cross-origin "Script error." stubs — these come from
      // third-party scripts (analytics, tag manager) where we can't
      // see the real error and can't fix it.
      if (!ev.message || ev.message === "Script error.") return;
      // Record BEFORE reloading. A stale-chunk failure used to reload and
      // report nothing, so a deploy that stranded clients left no trace at
      // all. `classifyErrorLevel` files it as a warning, so it stays out of
      // the "errors in the last 24h" badge while remaining visible.
      reportError({
        source: "client",
        trigger: "window-error",
        message: ev.message,
        stack: ev.error instanceof Error ? ev.error.stack ?? null : null,
        // Stripped here as well as at the `reportError` choke point: this site is
        // the one that can hold a document URL (`ev.filename` is empty for an
        // inline handler), and a local strip is cheaper to audit than a proof
        // about what the callee does.
        url: stripUrlQueryForReport(ev.filename || window.location.href),
        context: {
          line: ev.lineno,
          col: ev.colno,
        },
      });
      // Stale-deploy chunk failure → reload once to pick up the new version.
      // Pass the stack too: a chunk signature sometimes appears only there.
      reloadOnceForChunkError(
        ev.message,
        ev.error instanceof Error ? ev.error.stack ?? null : null
      );
    }
    function onRejection(ev: PromiseRejectionEvent) {
      const reason = ev.reason;
      const message = describeRejection(reason);
      const stack = reason instanceof Error ? reason.stack ?? null : null;
      reportError({
        source: "unhandled-rejection",
        trigger: "unhandled-rejection",
        message,
        stack,
      });
      // A rejected dynamic import() (stale chunk) lands here — reload once.
      reloadOnceForChunkError(message, stack);
    }

    /**
     * Failed app bundles: a `/_next/static/` script or stylesheet that 404s.
     *
     * These fire an `error` event on the ELEMENT which does NOT bubble and is a
     * plain Event with no `message`, so the `onError` handler above never saw
     * them (it early-returns on an empty message). Capture phase is required
     * precisely because they don't bubble.
     *
     * DELIBERATELY NARROW — `/_next/static/` only. Reporting every failed
     * sub-resource sounds better but would be a self-inflicted flood: an
     * ad-blocker kills Google Tag Manager, Clarity and Turnstile on a large
     * share of real visits, and images get cancelled by navigation, so we'd
     * POST 1-3 rows per page load for much of our traffic. Each costs a Worker
     * invocation plus a D1 read+write, and — because `app_errors` is pruned at
     * a soft cap — that junk would EVICT REAL ERRORS. That is precisely the
     * failure this whole review exists to eliminate, so broken third-party
     * scripts and broken product images are deliberately NOT tracked here.
     *
     * What this does buy us is the definitive stale-deploy signal: a missing
     * chunk file IS the proof `isStaleChunkError` needs before it will trust
     * the otherwise-ambiguous minified module-factory message.
     */
    function onResourceError(ev: Event) {
      const el = ev.target as (HTMLElement & { src?: string; href?: string }) | null;
      // `ev.target` is `window` for ordinary JS exceptions, which the `onError`
      // handler already covers — `tagName` would be undefined there anyway.
      if (!el || el === (window as unknown as HTMLElement)) return;
      const tag = el.tagName?.toLowerCase();
      if (tag !== "script" && tag !== "link") return;
      const src = el.src || el.href || "";
      if (!src.includes("/_next/static/")) return;
      markChunkLoadFailure();
      reportError({
        // The page often still works (Next retries some chunk loads), and the
        // reload self-heal handles the rest — so `warning`, not `error`.
        level: "warning",
        source: "client",
        trigger: "resource-load",
        message: `Resource failed to load: <${tag}> ${src.slice(0, 300)}`,
        url: src,
        context: { resource: tag, staleDeploy: true },
      });
    }

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("error", onResourceError, { capture: true });
    // Deliver anything stranded by a previous offline session or a 429.
    window.addEventListener("online", flushQueue);
    flushQueue();
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("error", onResourceError, { capture: true });
      window.removeEventListener("online", flushQueue);
    };
  }, []);
  return null;
}
